'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { Toaster } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface LoginForm {
  email: string;
  password: string;
  rememberMe: boolean;
}

// ─── Animated Network Diagram ────────────────────────────────────────────────
function NetworkDiagram() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 50);
    return () => clearInterval(id);
  }, []);

  // Module cards: label, icon path, position (cx, cy) relative to 600x520 viewBox
  const modules = [
    { id: 'ehr',       label: ['Unified', 'EHR'],       cx: 230, cy: 90,  icon: 'ehr' },
    { id: 'tele1',    label: ['Telemedicine', ''],       cx: 420, cy: 60,  icon: 'tele' },
    { id: 'billing',  label: ['Medical', 'Billing'],     cx: 130, cy: 230, icon: 'billing' },
    { id: 'tele2',    label: ['Telemedicine', ''],       cx: 130, cy: 370, icon: 'tele2' },
    { id: 'labs',     label: ['Laboratories', ''],       cx: 230, cy: 490, icon: 'labs' },
    { id: 'portal',   label: ['Patient', 'Portal'],      cx: 390, cy: 460, icon: 'portal' },
  ];

  const centerX = 310;
  const centerY = 280;

  // Pulse dot progress per connection (0..1)
  const pulseProgress = (tick * 0.012) % 1;

  function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }

  return (
    <svg viewBox="0 0 600 560" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        {/* Background radial glow */}
        <radialGradient id="bgGlow" cx="55%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#d4eae8" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#f0f7f7" stopOpacity="0" />
        </radialGradient>
        {/* Center gear glow */}
        <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ff6b6b" stopOpacity="0.5" />
          <stop offset="60%" stopColor="#cc3333" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#cc3333" stopOpacity="0" />
        </radialGradient>
        {/* Card shadow */}
        <filter id="cardShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#2d6a6a" floodOpacity="0.12" />
        </filter>
        {/* Pulse dot glow */}
        <radialGradient id="pulseGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#cc3333" stopOpacity="1" />
          <stop offset="100%" stopColor="#cc3333" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Background */}
      <rect width="600" height="560" fill="#f5fafa" />
      <ellipse cx="330" cy="280" rx="220" ry="200" fill="url(#bgGlow)" />

      {/* Circuit board subtle lines */}
      {[80, 160, 240, 320, 400, 480].map((y) => (
        <line key={`h${y}`} x1="0" y1={y} x2="600" y2={y} stroke="#c8dede" strokeWidth="0.4" strokeDasharray="4 8" opacity="0.5" />
      ))}
      {[60, 140, 220, 300, 380, 460, 540].map((x) => (
        <line key={`v${x}`} x1={x} y1="0" x2={x} y2="560" stroke="#c8dede" strokeWidth="0.4" strokeDasharray="4 8" opacity="0.5" />
      ))}

      {/* ECG waveform */}
      <polyline
        points="320,280 340,280 348,260 356,300 364,255 372,305 380,280 420,280 440,280 448,265 456,295 464,260 472,300 480,280 520,280"
        fill="none" stroke="#b0cccc" strokeWidth="1.5" opacity="0.6"
      />

      {/* Connection lines */}
      {modules.map((m) => (
        <line
          key={`line-${m.id}`}
          x1={m.cx + 45} y1={m.cy + 35}
          x2={centerX} y2={centerY}
          stroke="#a8cccc" strokeWidth="1.5" strokeDasharray="5 4"
          opacity="0.7"
        />
      ))}

      {/* Animated pulse dots on lines */}
      {modules.map((m, i) => {
        const prog = ((pulseProgress + i * 0.16) % 1);
        const sx = m.cx + 45, sy = m.cy + 35;
        const ex = centerX, ey = centerY;
        const px = lerp(sx, ex, prog);
        const py = lerp(sy, ey, prog);
        return (
          <g key={`pulse-${m.id}`}>
            <circle cx={px} cy={py} r="5" fill="url(#pulseGlow)" opacity="0.5" />
            <circle cx={px} cy={py} r="3" fill="#cc3333" opacity="0.9" />
          </g>
        );
      })}

      {/* Center gear */}
      <circle cx={centerX} cy={centerY} r="52" fill="url(#centerGlow)" />
      <circle cx={centerX} cy={centerY} r="38" fill="white" filter="url(#cardShadow)" />
      {/* Gear teeth */}
      {Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * 45 * Math.PI) / 180;
        const tx = centerX + Math.cos(angle) * 38;
        const ty = centerY + Math.sin(angle) * 38;
        return (
          <rect
            key={`tooth-${i}`}
            x={tx - 4} y={ty - 4} width="8" height="8"
            fill="#2d6a6a" rx="1"
            transform={`rotate(${i * 45}, ${tx}, ${ty})`}
          />
        );
      })}
      <circle cx={centerX} cy={centerY} r="26" fill="#2d6a6a" />
      <circle cx={centerX} cy={centerY} r="10" fill="#ff8080" />
      {/* Gear inner lines */}
      {Array.from({ length: 6 }).map((_, i) => {
        const a = (i * 60 * Math.PI) / 180;
        return (
          <line key={`gl-${i}`}
            x1={centerX + Math.cos(a) * 10} y1={centerY + Math.sin(a) * 10}
            x2={centerX + Math.cos(a) * 22} y2={centerY + Math.sin(a) * 22}
            stroke="white" strokeWidth="2" opacity="0.6"
          />
        );
      })}

      {/* Module cards */}
      {modules.map((m) => (
        <g key={`card-${m.id}`} filter="url(#cardShadow)">
          <rect x={m.cx} y={m.cy} width="90" height="70" rx="12" fill="white" stroke="#d0e4e4" strokeWidth="1" />
          {/* Icon area */}
          <rect x={m.cx + 8} y={m.cy + 8} width="38" height="32" rx="8" fill="#eef6f6" />
          {/* Module-specific icons */}
          {m.icon === 'ehr' && (
            <>
              <path d={`M${m.cx+22},${m.cy+18} a5,5 0 0,1 10,0 a5,5 0 0,1 -10,0`} fill="none" stroke="#2d6a6a" strokeWidth="1.5" />
              <path d={`M${m.cx+20},${m.cy+22} q7,6 12,0`} fill="none" stroke="#2d6a6a" strokeWidth="1.5" />
              <circle cx={m.cx+38} cy={m.cy+32} r="7" fill="#2d6a6a" />
              <path d={`M${m.cx+35},${m.cy+32} l3,3 5,-5`} fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </>
          )}
          {m.icon === 'tele' && (
            <>
              <rect x={m.cx+12} y={m.cy+12} width="24" height="18" rx="3" fill="none" stroke="#2d6a6a" strokeWidth="1.5" />
              <path d={`M${m.cx+36},${m.cy+17} l8,-4 0,12 -8,-4`} fill="#2d6a6a" />
              <circle cx={m.cx+38} cy={m.cy+32} r="7" fill="#2d6a6a" />
              <path d={`M${m.cx+35},${m.cy+32} l3,3 5,-5`} fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </>
          )}
          {m.icon === 'billing' && (
            <>
              <rect x={m.cx+12} y={m.cy+10} width="26" height="22" rx="3" fill="none" stroke="#2d6a6a" strokeWidth="1.5" />
              <text x={m.cx+22} y={m.cy+25} fontSize="10" fill="#2d6a6a" fontWeight="bold">$</text>
              <line x1={m.cx+16} y1={m.cy+28} x2={m.cx+34} y2={m.cy+28} stroke="#2d6a6a" strokeWidth="1" />
              <circle cx={m.cx+38} cy={m.cy+32} r="7" fill="#2d6a6a" />
              <path d={`M${m.cx+35},${m.cy+32} l3,3 5,-5`} fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </>
          )}
          {m.icon === 'tele2' && (
            <>
              <circle cx={m.cx+24} cy={m.cy+18} r="7" fill="none" stroke="#2d6a6a" strokeWidth="1.5" />
              <path d={`M${m.cx+14},${m.cy+32} q10,-6 20,0`} fill="none" stroke="#2d6a6a" strokeWidth="1.5" />
              <circle cx={m.cx+38} cy={m.cy+32} r="7" fill="#2d6a6a" />
              <path d={`M${m.cx+35},${m.cy+32} l3,3 5,-5`} fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </>
          )}
          {m.icon === 'labs' && (
            <>
              <text x={m.cx+13} y={m.cy+30} fontSize="16" fill="#2d6a6a" fontWeight="bold" fontStyle="italic">Rx</text>
            </>
          )}
          {m.icon === 'portal' && (
            <>
              <rect x={m.cx+12} y={m.cy+12} width="26" height="18" rx="3" fill="none" stroke="#2d6a6a" strokeWidth="1.5" />
              <circle cx={m.cx+25} cy={m.cy+16} r="3" fill="#2d6a6a" />
              <path d={`M${m.cx+19},${m.cy+26} q6,-4 12,0`} fill="none" stroke="#2d6a6a" strokeWidth="1.2" />
              <circle cx={m.cx+38} cy={m.cy+32} r="7" fill="#2d6a6a" />
              <path d={`M${m.cx+35},${m.cy+32} l3,3 5,-5`} fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </>
          )}
          {/* Label */}
          {m.label.map((line, li) => line && (
            <text key={li} x={m.cx + 45} y={m.cy + 48 + li * 12} fontSize="9" fill="#2d5a5a" textAnchor="middle" fontWeight="600">
              {line}
            </text>
          ))}
        </g>
      ))}

      {/* Integrated Sector Workflows panel */}
      <g>
        <rect x="390" y="310" width="200" height="200" rx="12" fill="white" stroke="#d0e4e4" strokeWidth="1" filter="url(#cardShadow)" />
        <text x="490" y="335" fontSize="11" fill="#1a3a3a" textAnchor="middle" fontWeight="700">Integrated Sector Workflows</text>
        {[
          { icon: '⚕', label: 'MEDICAL', y: 360 },
          { icon: '🦷', label: 'DENTISTRY', y: 385 },
          { icon: '🚶', label: 'PHYSICAL REHAB', y: 410 },
          { icon: '🧪', label: 'LABORATORIES', y: 435 },
          { icon: '⚙', label: 'MEDTECH IOT HUB', y: 460 },
        ].map((item) => (
          <g key={item.label}>
            <text x="405" y={item.y} fontSize="11" fill="#2d6a6a">{item.icon}</text>
            <text x="422" y={item.y} fontSize="9.5" fill="#1a3a3a" fontWeight="600">{item.label}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}

// ─── Main Login Page ──────────────────────────────────────────────────────────
export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { signIn } = useAuth();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const handleLogin = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back — redirecting to dashboard');
      setTimeout(() => router.push('/clinical-dashboard'), 800);
    } catch (err: any) {
      setIsLoading(false);
      toast.error(err?.message || 'Invalid email or password. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f0f7f7]">
      <Toaster position="bottom-right" />

      {/* ── Top Navigation Bar ── */}
      <header className="w-full bg-white border-b border-gray-100 px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/LOGOWHITE-1773958149092.png"
            alt="ProactiveMED+"
            className="h-12 w-auto object-contain"
          />
        </div>

        {/* Nav links */}
        <nav className="flex items-center gap-6">
          <a href="#" className="text-sm text-gray-600 hover:text-[#2d6a6a] transition-colors font-medium">Help Center</a>
          <a href="#" className="text-sm text-gray-600 hover:text-[#2d6a6a] transition-colors font-medium">Contact Support</a>
          <a
            href="#"
            className="px-5 py-2 bg-[#1a3a3a] text-white text-sm font-semibold rounded-full hover:bg-[#2d6a6a] transition-colors"
          >
            Request a Demo
          </a>
        </nav>
      </header>

      {/* ── Main Content ── */}
      <main className="flex-1 flex flex-col lg:flex-row">
        {/* Left: Login Form */}
        <div className="flex-1 flex items-center justify-center px-8 py-12 lg:max-w-[45%]">
          {/* Background network dots (left side) */}
          <div className="absolute left-0 top-16 bottom-0 w-[40%] pointer-events-none overflow-hidden">
            <svg viewBox="0 0 400 600" className="w-full h-full opacity-30" xmlns="http://www.w3.org/2000/svg">
              {/* Network nodes */}
              {[
                [40, 120], [120, 80], [200, 150], [80, 250], [160, 320],
                [40, 400], [120, 460], [200, 380], [80, 520], [160, 200],
                [240, 280], [300, 180], [260, 420], [320, 350],
              ].map(([x, y], i) => (
                <g key={i}>
                  <circle cx={x} cy={y} r={i % 3 === 0 ? 8 : 5} fill={i % 4 === 0 ? '#cc3333' : '#2d6a6a'} opacity={i % 4 === 0 ? 0.6 : 0.4} />
                  {i % 4 === 0 && <circle cx={x} cy={y} r="14" fill="#cc3333" opacity="0.15" />}
                </g>
              ))}
              {/* Connection lines */}
              {[
                [40,120,120,80],[120,80,200,150],[40,120,80,250],[80,250,160,320],
                [160,320,40,400],[40,400,120,460],[120,460,200,380],[200,380,80,520],
                [160,200,240,280],[240,280,300,180],[240,280,260,420],[260,420,320,350],
              ].map(([x1,y1,x2,y2], i) => (
                <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#2d6a6a" strokeWidth="1" opacity="0.3" />
              ))}
            </svg>
          </div>

          <div className="w-full max-w-md relative z-10">
            {/* Login Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8">
              <h1 className="text-2xl font-black text-[#1a3a3a] tracking-wide mb-2 uppercase">
                Secure Portal Login
              </h1>
              <p className="text-gray-500 text-sm mb-7 leading-relaxed">
                Enter your credentials to access your unified healthcare platform.
              </p>

              <form onSubmit={handleSubmit(handleLogin)} className="space-y-4">
                {/* Email */}
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                        d="M12 4.5c-4.5 0-7.5 3-7.5 7.5 0 3 1.5 5.5 4 6.5M12 4.5c4.5 0 7.5 3 7.5 7.5 0 3-1.5 5.5 4 6.5M12 4.5v15M8 9l4-4 4 4" />
                      <circle cx="12" cy="12" r="3" strokeWidth={1.5} />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12a3 3 0 0 0 6 0" />
                    </svg>
                  </div>
                  <input
                    {...register('email', { required: 'Email is required' })}
                    type="email"
                    placeholder="Email Address"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2d6a6a] focus:border-transparent placeholder-gray-400"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>

                {/* Password */}
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="5" y="11" width="14" height="10" rx="2" strokeWidth={1.5} strokeLinecap="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 11v12a4 4 0 0 0 8 0V11" />
                      <circle cx="12" cy="16" r="1.5" fill="currentColor" />
                    </svg>
                  </div>
                  <input
                    {...register('password', { required: 'Password is required' })}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2d6a6a] focus:border-transparent placeholder-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3l18 18M10.5 10.5A3 3 0 0 0 13.5 13.5M6.5 6.5A9.77 9.77 0 0 0 3 12c2 4 5.5 7 9 7a9.77 9.77 0 0 0 5.5-1.5M9 5.1A9.77 9.77 0 0 1 12 5c3.5 0 7 3 9 7a9.77 9.77 0 0 1-1.5 2.5" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        <circle cx="12" cy="12" r="3" strokeWidth={1.5} />
                      </svg>
                    )}
                  </button>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                </div>

                {/* Forgot Password */}
                <div className="flex justify-end">
                  <button type="button" className="text-sm text-[#cc3333] hover:text-[#aa2222] font-medium">
                    Forgot Password?
                  </button>
                </div>

                {/* Remember Me */}
                <div className="flex items-center gap-2">
                  <input
                    {...register('rememberMe')}
                    type="checkbox"
                    id="rememberMe"
                    className="w-4 h-4 border-2 border-gray-300 rounded accent-[#2d6a6a]"
                  />
                  <label htmlFor="rememberMe" className="text-sm text-gray-600 cursor-pointer">
                    Remember Me
                  </label>
                </div>

                {/* Sign In Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 bg-[#1a3a3a] hover:bg-[#2d6a6a] text-white font-bold text-sm tracking-widest rounded-xl transition-colors disabled:opacity-60 uppercase"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Signing In...
                    </span>
                  ) : 'Sign In'}
                </button>

                {/* SSO */}
                <p className="text-center text-sm text-gray-600 font-medium pt-1">
                  Alternatively, use Single Sign-On (SSO)
                </p>
              </form>
            </div>
          </div>
        </div>

        {/* Right: Network Diagram */}
        <div className="flex-1 relative flex items-center justify-center py-8 px-4 min-h-[500px]">
          <div className="w-full max-w-2xl h-full min-h-[480px]">
            <NetworkDiagram />
          </div>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="bg-[#1a3a3a] text-white px-8 py-5">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-4">
          {/* Contact info */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 text-sm">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="text-gray-300">+1 210-537-5832</span>
            </div>
            <div className="flex items-center gap-2">
              {/* WhatsApp icon */}
              <svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.297.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span className="text-gray-300">+1 210-537-5832</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth={1.5} />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
              </svg>
              <span className="text-gray-300">www.proactivomed.com</span>
            </div>
          </div>

          {/* App store badges + website/email */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* App Store */}
            <a href="#" className="flex items-center gap-2 px-3 py-1.5 bg-black border border-gray-600 rounded-lg hover:bg-gray-900 transition-colors">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <div className="text-left">
                <div className="text-[9px] text-gray-400 leading-none">Download on the</div>
                <div className="text-xs text-white font-semibold leading-tight">App Store</div>
              </div>
            </a>
            {/* Google Play */}
            <a href="#" className="flex items-center gap-2 px-3 py-1.5 bg-black border border-gray-600 rounded-lg hover:bg-gray-900 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path d="M3 20.5v-17c0-.83 1-.83 1.5-.5l14 8.5-14 8.5c-.5.33-1.5.33-1.5-.5z" fill="#4CAF50" />
                <path d="M3 3.5l9 9-9 9V3.5z" fill="#81C784" />
                <path d="M12 12.5l5.5 3.5-5.5 9v-5.5z" fill="#FF9800" />
                <path d="M22 12.6h-9.4V22H22v-9.4z" fill="#F44336" />
              </svg>
              <div className="text-left">
                <div className="text-[9px] text-gray-400 leading-none">GET IT ON</div>
                <div className="text-xs text-white font-semibold leading-tight">Google Play</div>
              </div>
            </a>
            {/* Website + Email */}
            <div className="flex flex-col gap-1 text-sm">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" strokeWidth={1.5} />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                </svg>
                <span className="text-gray-300">www.proactivomed.com</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                </svg>
                <span className="text-gray-300">ernesto@nobisys.com</span>
              </div>
            </div>
          </div>
        </div>
        <div className="text-center mt-3 text-xs text-gray-500">A PUMS Company</div>
      </footer>
    </div>
  );
}